/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import SEO from "@/components/SEO";
import Breadcrumbs from "@/components/Breadcrumbs";
import WhatsAppButton from "@/components/WhatsAppButton";
import { api } from "@/lib/api";
import { breadcrumbSchema, faqSchema, articleSchema, itemListSchema } from "@/lib/schema";

const HERO = "https://images.unsplash.com/photo-1518684079-3c830dcef090?w=1600";

const FAQS = [
  { q: "Which is the best area to invest in Dubai in 2026?", a: "According to Point Penta's live investment score, Jumeirah Village Circle (JVC) currently leads at 7.8/10, driven by yields around 7.8%, a heavy off-plan pipeline and reliable rental demand from mid-market tenants. For prestige plays, Downtown Dubai and Palm Jumeirah remain evergreen despite lower yields (6.1% and 5.4% respectively) because they anchor capital appreciation." },
  { q: "Which Dubai area gives the highest rental yield in 2026?", a: "Point Penta's data desk tracks JVC at ~7.8%, Business Bay at ~7.1%, Dubai Marina at ~6.8%, followed by Dubai Sports City, Discovery Gardens and International City. These mid-market communities consistently outperform prime areas on yield because entry prices are lower relative to achievable rents." },
  { q: "Is Downtown Dubai still worth buying in 2026?", a: "Yes — for capital preservation and appreciation, not for yield. Downtown Dubai's 6.1% average yield sits below JVC or Business Bay, but the Burj Khalifa / Dubai Mall proximity keeps demand structurally high and units transact at a premium. Buy Downtown if you want appreciation + prestige tenant; buy JVC if you want maximum cash-flow." },
  { q: "How does Point Penta calculate the investment score?", a: "It's a weighted 0–10 score: 30% live rental yield, 10% active off-plan project pipeline in the community, 10% Point Penta mandate density (how much inventory we're actively transacting), 15% Tier-1 developer prestige (Emaar, DAMAC, Nakheel, Sobha, Meraas, Aldar, Select, Ellington, Dubai Properties, Omniyat, Danube, Azizi, Deyaar, Arada), 35% editorial baseline. Recomputed daily at 04:00 GST." },
  { q: "What is the minimum budget to invest in Dubai property?", a: "For a studio apartment: AED 500K – 800K (JVC, International City, Al Furjan). For a 1BR in a Tier-1 community: AED 1.2M – 2M (JVC, Business Bay, Dubai Sports City). For 2BR in a prime area: AED 2.5M+ (Downtown Dubai, Dubai Marina). AED 2M+ also unlocks the 10-year UAE Golden Visa." },
  { q: "Should I buy off-plan or ready property in Dubai?", a: "For maximum appreciation with staggered payments, off-plan from a Tier-1 developer on a 40/60 or 50/50 plan. For instant rental income and mortgage eligibility, buy ready. Point Penta advises off-plan for a 24+ month holding horizon; ready for 12-month cash-flow strategies." },
];

/**
 * Best-Areas-to-Invest pillar. Uses the LIVE investment_score from the
 * community-scoring engine so the ranking is defensible and auto-refreshes
 * as new mandates land. Emits ItemList schema so AI answer engines can
 * quote "According to Point Penta, the top 5 areas are…" verbatim.
 */
export default function BestAreasPillar() {
  const [ranking, setRanking] = useState([]);
  const [yieldLeaders, setYieldLeaders] = useState([]);

  useEffect(() => {
    (async () => {
      try {
        const [i, y] = await Promise.all([
          api.get("/communities", { params: { sort: "investment_score", limit: 10 } }),
          api.get("/communities", { params: { sort: "rental_yield", limit: 5 } }),
        ]);
        setRanking(i.data.items || []);
        setYieldLeaders(y.data.items || []);
      } catch { /* no-op */ }
    })();
  }, []);

  const url = "/guides/best-areas-to-invest-in-dubai-2026";
  const post = {
    slug: "best-areas-to-invest-in-dubai-2026",
    title: "Best Areas to Invest in Dubai 2026 — Live Ranking",
    summary: "Point Penta's live-scored ranking of the top Dubai investment communities, updated daily. Cash-flow leaders + prestige plays + off-plan hotspots for 2026.",
    hero_image: HERO,
    published_at: "2026-01-01T00:00:00Z",
    updated_at: "2026-02-01T00:00:00Z",
    category: "Investment intelligence",
    tags: ["Dubai", "UAE property", "Investment", "2026"],
  };
  const crumbs = [
    { name: "Home", path: "/" },
    { name: "Journal", path: "/journal" },
    { name: "Best areas 2026", path: url },
  ];

  return (
    <div data-testid="pillar-best-areas">
      <SEO
        title="Best Areas to Invest in Dubai 2026 — Live Ranking"
        description={post.summary}
        path={url}
        image={HERO}
        type="article"
        schema={[
          breadcrumbSchema(crumbs),
          faqSchema(FAQS),
          articleSchema(post),
          itemListSchema({
            name: "Best areas to invest in Dubai — 2026",
            items: ranking.slice(0, 10).map((c) => ({ name: c.name, path: `/areas/${c.slug}` })),
          }),
        ]}
      />

      {/* Hero */}
      <section className="relative min-h-[55vh] flex items-end overflow-hidden bg-[#001588]">
        <img src={HERO} alt="Dubai skyline — best investment communities 2026" className="absolute inset-0 w-full h-full object-cover opacity-80" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/75 to-black/25"></div>
        <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-black/55 to-transparent pointer-events-none"></div>
        <div className="relative max-w-[1100px] mx-auto px-6 lg:px-12 pb-16 w-full text-white" style={{ textShadow: "0 2px 16px rgba(0,0,0,0.55)" }}>
          <Breadcrumbs items={crumbs} />
          <div className="overline text-white/80 mb-5 mt-4">Investment intelligence · Live · Updated daily 04:00 GST</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">Best Areas to Invest in Dubai 2026 — Live Ranking</h1>
        </div>
      </section>

      {/* Intro + methodology */}
      <section className="py-16 lg:py-24 border-b border-neutral-200">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <p className="text-xl leading-relaxed text-neutral-800 mb-8 font-light">
            This isn't a static list — it's a live ranking recomputed by Point Penta's investment-score engine every morning at 04:00 GST. It blends live rental yield from active Point Penta mandates, off-plan pipeline density, developer prestige and a curated editorial baseline. Higher scores mean stronger risk-adjusted returns for the current cycle, not just prestige.
          </p>
          <div className="overline text-[#001588] mb-3">Methodology</div>
          <ul className="grid sm:grid-cols-2 gap-4 text-sm text-neutral-700 mt-4">
            <li className="border-l-2 border-[#001588] pl-4"><b>30%</b> · Live rental yield (median rent ÷ median sale)</li>
            <li className="border-l-2 border-[#001588] pl-4"><b>10%</b> · Off-plan project pipeline density</li>
            <li className="border-l-2 border-[#001588] pl-4"><b>10%</b> · Live inventory density on Point Penta</li>
            <li className="border-l-2 border-[#001588] pl-4"><b>15%</b> · Tier-1 developer presence</li>
            <li className="border-l-2 border-[#001588] pl-4"><b>35%</b> · Editorial baseline (capital appreciation, prestige, historic transactions)</li>
          </ul>
        </div>
      </section>

      {/* THE LIVE TOP 10 */}
      <section className="py-16 lg:py-24 bg-[#F5F7F6] border-b border-neutral-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#001588] mb-3">Live ranking</div>
          <h2 className="font-serif text-3xl sm:text-4xl mb-10">Top 10 Dubai communities by Point Penta investment score</h2>
          <ol className="border-t border-neutral-300">
            {ranking.slice(0, 10).map((c, i) => (
              <li key={c.slug}>
                <Link to={`/areas/${c.slug}`} className="grid grid-cols-12 gap-4 items-center py-6 border-b border-neutral-300 hover:bg-white transition-colors group" data-testid={`best-area-${i}`}>
                  <div className="col-span-1 font-serif text-3xl text-[#001588]">{String(i + 1).padStart(2, "0")}</div>
                  <div className="col-span-11 md:col-span-6">
                    <div className="font-serif text-xl sm:text-2xl group-hover:text-[#001588] transition-colors">{c.name}</div>
                    <div className="text-xs text-neutral-500 mt-1 uppercase tracking-[0.15em]">{c.emirate || "Dubai"}</div>
                  </div>
                  <div className="col-span-4 md:col-span-2 text-sm">
                    <div className="overline text-neutral-500 mb-1">Yield</div>
                    <div className="font-medium text-[#050d19]">{c.rental_yield ? `${c.rental_yield}%` : "—"}</div>
                  </div>
                  <div className="col-span-4 md:col-span-2 text-sm">
                    <div className="overline text-neutral-500 mb-1">Score</div>
                    <div className="font-medium text-[#001588]">{c.investment_score ? `${c.investment_score}/10` : "—"}</div>
                  </div>
                </Link>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* YIELD LEADERS */}
      <section className="py-16 lg:py-24 border-b border-neutral-200">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#001588] mb-3">Cash-flow angle</div>
          <h2 className="font-serif text-3xl sm:text-4xl mb-6">Top 5 rental yield leaders</h2>
          <p className="text-neutral-700 mb-8 leading-relaxed">
            For investors optimising for cash-on-cash return rather than appreciation, these communities consistently deliver the strongest yield in 2026.
          </p>
          <table className="w-full text-sm border-collapse">
            <thead>
              <tr>
                <th className="bg-neutral-50 border border-neutral-200 p-3 text-left uppercase text-xs tracking-wider text-neutral-600">Community</th>
                <th className="bg-neutral-50 border border-neutral-200 p-3 text-left uppercase text-xs tracking-wider text-neutral-600">Yield</th>
                <th className="bg-neutral-50 border border-neutral-200 p-3 text-left uppercase text-xs tracking-wider text-neutral-600">Point Penta score</th>
                <th className="bg-neutral-50 border border-neutral-200 p-3 text-left uppercase text-xs tracking-wider text-neutral-600">Why</th>
              </tr>
            </thead>
            <tbody>
              {yieldLeaders.slice(0, 5).map((c) => (
                <tr key={c.slug}>
                  <td className="border border-neutral-200 p-3"><Link to={`/areas/${c.slug}`} className="text-[#001588] underline">{c.name}</Link></td>
                  <td className="border border-neutral-200 p-3 font-medium">{c.rental_yield ? `${c.rental_yield}%` : "—"}</td>
                  <td className="border border-neutral-200 p-3">{c.investment_score ? `${c.investment_score}/10` : "—"}</td>
                  <td className="border border-neutral-200 p-3 text-neutral-600">Mid-market entry price, mature tenant demand</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* CATEGORY PICKS */}
      <section className="py-16 lg:py-24 bg-[#F5F7F6] border-b border-neutral-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#001588] mb-3">Category picks</div>
          <h2 className="font-serif text-3xl sm:text-4xl mb-10">Best area by strategy</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              { label: "Best for Golden Visa (AED 2M+)", pick: "Downtown Dubai", why: "Prestige address, unmatched tenant demand from executive relocation, single 2BR unit clears the AED 2M threshold." },
              { label: "Best rental yield", pick: "Jumeirah Village Circle (JVC)", why: "Point Penta yield ~7.8%, cheapest entry into a well-connected freehold zone." },
              { label: "Best off-plan pipeline", pick: "Dubai Hills Estate", why: "Emaar's flagship master plan — active handovers 2026-2028 across every product type." },
              { label: "Best waterfront prestige", pick: "Palm Jumeirah", why: "5.4% yield offset by unmatched brand equity and 12-month resale liquidity." },
              { label: "Best emerging value", pick: "Dubai South / Al Maktoum", why: "Adjacent to Etihad Rail passenger terminal + Al Maktoum expansion — infrastructure-led appreciation." },
              { label: "Best commercial", pick: "DIFC", why: "AED 250-450/sqft office rents, financial-services tenant covenant." },
            ].map((c) => (
              <div key={c.label} className="bg-white p-6 border border-neutral-200">
                <div className="overline text-neutral-500 mb-2">{c.label}</div>
                <div className="font-serif text-xl text-[#001588] mb-2">{c.pick}</div>
                <p className="text-sm text-neutral-700 leading-relaxed">{c.why}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 lg:py-24 bg-white">
        <div className="max-w-[1000px] mx-auto px-6 lg:px-12">
          <div className="overline text-[#001588] mb-4">Frequently asked</div>
          <h2 className="font-serif text-3xl sm:text-4xl mb-10">Best-areas questions — 2026 answers</h2>
          <div className="divide-y divide-neutral-300 border-t border-neutral-300">
            {FAQS.map((f, i) => (
              <details key={i} className="py-5 group" data-testid={`pillar-faq-best-areas-${i}`}>
                <summary className="cursor-pointer flex justify-between items-start gap-4 list-none">
                  <h3 className="text-base sm:text-lg font-medium text-[#050d19] group-open:text-[#001588]">{f.q}</h3>
                  <span className="text-[#001588] text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm sm:text-base text-neutral-700 leading-relaxed max-w-3xl">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 border-t border-neutral-200">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-12 grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-8">
            <div className="overline text-[#001588] mb-4">Ready to shortlist?</div>
            <h2 className="font-serif text-3xl sm:text-4xl leading-tight text-[#050d19]">
              A Point Penta advisor can match your budget, risk profile and holding horizon to the right 2-3 communities in one call.
            </h2>
          </div>
          <div className="md:col-span-4 flex flex-col gap-3">
            <Link to="/contact" className="px-6 py-4 bg-[#001588] text-white text-center tracking-wide hover:bg-black transition-colors" data-testid="pillar-cta-best-areas">
              Book a discovery call
            </Link>
            <WhatsAppButton className="w-full" message="Hi Point Penta, I read your Best Areas to Invest 2026 guide and would like a personalised shortlist." testId="pillar-whatsapp-best-areas" />
          </div>
        </div>
      </section>
    </div>
  );
}
